import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';

import {
  Badge,
  Button,
  EmptyState,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Select,
  useConfirmDialog,
} from '@/components/ui';
// FASE-C: os rótulos das ações têm UMA fonte só — a mesma do editor. Antes
// este painel tinha a própria lista, com "WhatsApp (stub)" e "Email (stub)"
// na cara do admin.
import { ACOES_EM_PREPARACAO, ACTION_TYPE_LABELS } from './AcoesEditor';
import { cn } from '@/lib/cn';
import { formatRelativeDate, formatDateTime } from '@/lib/format-date';
import {
  useAutomations,
  useAutomationRuns,
  useAutomationActions,
  type Automation,
  type AutomationAction,
  type AutomationRun,
  type AutomationTrigger,
} from '@/lib/automations-store';
import type { Deal } from '@/lib/comercial-types';
import type { KanbanColumn } from '@/lib/use-kanban-columns';

// BUG-V2-058 / Sprint 3 (2026-05-25): dialog shared movido pra
// src/components/automations/. AutomationsPanel continua local porque é
// slide-in específico de Kanban workflow — não generalizamos prematuramente.
import { AutomationFormDialog } from '@/components/automations/AutomationFormDialog';
import { AutomationSandboxDialog } from '@/components/automations/AutomationSandboxDialog';

/**
 * Painel lateral de Automações do workflow Comercial — Regra 9 do Padrão
 * Kanban IGM, commit 6 da rodada feat/backend-first-foundation.
 *
 * Slide-in da direita, 480px wide. Tabs "Automações" | "Histórico".
 * Admin-only — caller (ComercialKanban) gateia.
 *
 * Estados completos: loading (cache vazio), empty, erro inline nas ações.
 */

export interface AutomationsPanelProps {
  open: boolean;
  onClose: () => void;
  /**
   * Workflow slug — R5.1 Bloco E relaxa de `'comercial'` literal pra string
   * pra suportar multi-workflow (Manutenção, etc). Engine `useAutomations`
   * já aceita string desde o início.
   */
  workflowSlug: string;
  /**
   * Deals/items pra usar no sandbox de teste. Opcional desde R5.1 Bloco E:
   * workflows sem fixture sandbox (Manutenção até existir OS sandbox) passam
   * undefined/[] e o sandbox mostra "Sem deals pra testar" gracefully.
   */
  deals?: Deal[];
  columns: KanbanColumn[];
  /** Resolve label de coluna (usa useColumnLabels do caller). */
  getColumnLabel: (slug: string) => string;
  /**
   * Título customizado do painel — default "Automações do fluxo".
   *
   * FASE-C: o default era `Automações do Fluxo {workflowSlug}`, ou seja, o
   * identificador interno do fluxo ("compras", "corretiva") em minúsculo no
   * meio do título. Quem quiser o nome bonito do fluxo passa `title`.
   */
  title?: string;
}

const TITULO_PADRAO = 'Automações do fluxo';

export function AutomationsPanel({
  open,
  onClose,
  workflowSlug,
  deals = [],
  columns,
  getColumnLabel,
  title,
}: AutomationsPanelProps) {
  // FASE 2 / M1: ConfirmDialog
  const confirm = useConfirmDialog();
  const automations = useAutomations(workflowSlug);
  const actions = useAutomationActions(workflowSlug);

  const [creatingOpen, setCreatingOpen] = useState(false);
  const [editing, setEditing] = useState<Automation | null>(null);
  const [sandboxFor, setSandboxFor] = useState<Automation | null>(null);
  const [historyFilter, setHistoryFilter] = useState<string>('all');

  const ativasCount = useMemo(
    () => automations.filter((a) => a.enabled).length,
    [automations]
  );

  // Esc fecha (mas só se nenhum modal interno aberto)
  useEffect(() => {
    if (!open) return;
    function handler(e: KeyboardEvent) {
      if (e.key === 'Escape' && !creatingOpen && !editing && !sandboxFor) onClose();
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, creatingOpen, editing, sandboxFor, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        data-testid="automations-panel-backdrop"
        aria-hidden="true"
        className="fixed inset-0 z-40 bg-ink-1/30 dark:bg-ink-dark-1/40 backdrop-blur-[2px] animate-fade-in"
        onClick={onClose}
      />
      <aside
        data-testid="automations-panel"
        role="dialog"
        aria-label={title ?? TITULO_PADRAO}
        className="fixed top-0 right-0 z-50 h-screen w-full sm:max-w-[480px] bg-paper-surface dark:bg-paper-dark-surface shadow-card-hover flex flex-col animate-slide-up"
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-line dark:border-line-dark flex-shrink-0">
          <div className="min-w-0">
            <h2 className="font-display text-xl text-ink-1 dark:text-ink-dark-1 truncate">
              {title ?? TITULO_PADRAO}
            </h2>
            <p className="text-xs text-ink-2 dark:text-ink-dark-2 mt-0.5">
              {ativasCount} ativa{ativasCount === 1 ? '' : 's'} · {automations.length}{' '}
              total
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar painel"
            data-testid="automations-panel-close"
            className="shrink-0 rounded p-1 text-ink-2 hover:text-ink-1 dark:text-ink-dark-2 dark:hover:text-ink-dark-1"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 pb-5">
          <Tabs defaultValue="automacoes">
            <TabsList className="mt-3 sticky top-0 bg-paper-surface dark:bg-paper-dark-surface z-10">
              <TabsTrigger value="automacoes">Automações</TabsTrigger>
              <TabsTrigger value="historico">Histórico</TabsTrigger>
            </TabsList>

            <TabsContent value="automacoes">
              <div className="flex justify-end mb-3">
                <Button
                  size="sm"
                  data-testid="automations-new"
                  onClick={() => setCreatingOpen(true)}
                >
                  + Nova automação
                </Button>
              </div>
              {automations.length === 0 ? (
                <EmptyState
                  title="Nenhuma automação ainda"
                  description="Uma automação faz o sistema agir sozinho quando um card entra numa coluna, sai dela ou fica parado tempo demais — avisar alguém, criar uma tarefa, mover o card."
                  action={
                    <Button size="sm" onClick={() => setCreatingOpen(true)}>
                      + Criar a primeira
                    </Button>
                  }
                />
              ) : (
                <ul className="space-y-3" data-testid="automations-list">
                  {automations.map((a) => (
                    <AutomationCard
                      key={a.id}
                      automation={a}
                      labelOf={getColumnLabel}
                      onEdit={() => setEditing(a)}
                      onToggle={() => actions.toggle(a.id, a.enabled)}
                      onDelete={async () => {
                        const ok = await confirm({
                          title: 'Excluir automação',
                          message: `Excluir a automação "${a.nome}"? O histórico de disparos dela também é apagado, e isso não tem volta.`,
                          confirmLabel: 'Excluir',
                          variant: 'destructive',
                        });
                        if (ok) void actions.remove(a.id);
                      }}
                      onTest={() => setSandboxFor(a)}
                    />
                  ))}
                </ul>
              )}
            </TabsContent>

            <TabsContent value="historico">
              <HistoryTab
                automations={automations}
                workflowSlug={workflowSlug}
                filterAutomationId={historyFilter}
                onFilterChange={setHistoryFilter}
              />
            </TabsContent>
          </Tabs>
        </div>
      </aside>

      {/* Wizard create — BUG-V2-058: shell shared com contexto kind=workflow. */}
      <AutomationFormDialog
        open={creatingOpen}
        onClose={() => setCreatingOpen(false)}
        contexto={{ kind: 'workflow', workflowSlug }}
        onSubmit={async (input) => {
          await actions.create(input);
        }}
        columns={columns}
        deals={deals}
        getColumnLabel={getColumnLabel}
      />

      {/* Wizard edit — mesmo contexto kind=workflow. */}
      {editing && (
        <AutomationFormDialog
          open
          onClose={() => setEditing(null)}
          contexto={{ kind: 'workflow', workflowSlug }}
          onSubmit={async (input) => {
            await actions.update(editing.id, input);
          }}
          columns={columns}
          deals={deals}
          initial={editing}
          automationId={editing.id}
          getColumnLabel={getColumnLabel}
        />
      )}

      {/* Sandbox solto (botão Testar do card) */}
      {sandboxFor && (
        <AutomationSandboxDialog
          open
          onClose={() => setSandboxFor(null)}
          automationId={sandboxFor.id}
          deals={deals}
        />
      )}
    </>
  );
}

// ─── Card de automação ────────────────────────────────────────────────

interface AutomationCardProps {
  automation: Automation;
  labelOf: (slug: string) => string;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
  onTest: () => void;
}

/**
 * FASE-C: o resumo do card virava jargão puro na tela — "Trigger
 * questionario_resposta", "Setar prioridade alta (pai)", "Agendar no calendar",
 * "Webhook POST host", "Notificar responsavel no app". Cada frase agora é
 * português, e nenhum identificador interno (slug, uuid, tipo do engine)
 * aparece pro admin.
 */
const ALVO_LABEL: Record<string, string> = {
  responsavel: 'o responsável pelo card',
  criador: 'quem criou o card',
  todos_no_card: 'todo mundo no card',
  user_specific: 'uma pessoa específica',
  cliente: 'o cliente',
  lista: 'uma lista de pessoas',
  round_robin: 'rodízio entre várias pessoas',
};
function alvo(target: string): string {
  return ALVO_LABEL[target] ?? target;
}

const CARD_ALVO_LABEL: Record<string, string> = {
  pai: 'neste card',
  filho_recem_criado: 'no card recém-criado',
};

const PRIORIDADE_LABEL: Record<string, string> = {
  baixa: 'baixa',
  normal: 'normal',
  alta: 'alta',
  urgente: 'urgente',
};

function triggerSummary(trigger: AutomationTrigger, labelOf: (slug: string) => string): string {
  // columnSlug ficou opcional após Bloco D.1 (Migration 033) pra suportar
  // triggers questionario_resposta/manual/webhook_in que não têm coluna.
  // Aqui usamos narrowing defensivo pra os 3 tipos kanban que esperam columnSlug.
  const columnSlug = trigger.config.columnSlug;
  const coluna = columnSlug ? `"${labelOf(columnSlug)}"` : 'uma coluna que não existe mais';
  if (trigger.type === 'card.entered_column') {
    return `Quando um card entra em ${coluna}`;
  }
  if (trigger.type === 'card.left_column') {
    return `Quando um card sai de ${coluna}`;
  }
  if (trigger.type === 'card.idle_in_column') {
    const d = trigger.config.days;
    return `Quando um card fica ${d} dia${d === 1 ? '' : 's'} parado em ${coluna}`;
  }
  // Bloco D.1 — triggers cross-workflow são editados em UI dedicada
  // (/admin/automacoes); aqui é só o resumo.
  if (trigger.type === 'questionario_resposta') {
    return 'Quando uma pergunta do questionário recebe a resposta combinada';
  }
  return 'Quando a condição configurada acontece';
}

function actionSummary(
  action: AutomationAction,
  labelOf: (slug: string) => string,
): string {
  const rotulo = ACTION_TYPE_LABELS[action.type];

  if (action.type === 'notify_in_app') {
    return `Avisar ${alvo(action.target)} no app`;
  }
  if (action.type === 'send_whatsapp' || action.type === 'send_email') {
    // As duas ainda não são enviadas de verdade — o rótulo canônico já diz
    // "em preparação", então aqui só completamos com o destinatário.
    return `${rotulo} — iria pra ${alvo(action.target)}`;
  }
  if (action.type === 'create_task') {
    const d = action.prazoDiasUteis;
    return `Criar uma tarefa pra ${alvo(action.target)}, com prazo de ${d} dia${d === 1 ? '' : 's'} útil${d === 1 ? '' : 'eis'}`;
  }
  if (action.type === 'move_card') {
    return `Mover o card pra "${labelOf(action.targetColumnSlug)}"`;
  }
  if (action.type === 'assign_responsavel') {
    if (action.strategy === 'fixed') return 'Definir sempre a mesma pessoa como responsável';
    if (action.strategy === 'round_robin') return 'Definir o responsável por rodízio';
    return 'Definir como responsável quem tem menos negócios em aberto';
  }
  if (action.type === 'criar_card_workflow') {
    return 'Abrir um card em outro fluxo';
  }
  if (action.type === 'setar_prioridade') {
    const p = PRIORIDADE_LABEL[action.novaPrioridade] ?? action.novaPrioridade;
    return `Mudar a prioridade pra ${p} ${CARD_ALVO_LABEL[action.cardAlvo] ?? ''}`.trim();
  }
  if (action.type === 'adicionar_tag') {
    return `Marcar com a tag "${action.tag}" ${CARD_ALVO_LABEL[action.cardAlvo] ?? ''}`.trim();
  }
  if (action.type === 'criar_atividade_card') {
    return `Registrar uma atividade ${CARD_ALVO_LABEL[action.cardAlvo] ?? ''}`.trim();
  }
  if (action.type === 'agendar_calendar') {
    return `Agendar no Google Agenda (${action.duracaoMinutes} min)`;
  }
  if (action.type === 'webhook_externo') {
    return 'Avisar um sistema externo';
  }
  if (action.type === 'celebrar_e_notificar') {
    if (action.scope === 'global') return 'Celebrar com a empresa inteira';
    if (action.scope === 'workflow') return 'Celebrar com todo mundo do fluxo';
    return 'Celebrar com o departamento do fluxo';
  }
  // Exhaustive check — TS sabe que aqui é unreachable
  const exhaustive: never = action;
  void exhaustive;
  return rotulo;
}

/** Uma ação que ainda não é enviada de verdade ganha aviso no card. */
function acaoEmPreparacao(action: AutomationAction): boolean {
  return ACOES_EM_PREPARACAO.includes(action.type);
}

function AutomationCard({
  automation,
  labelOf,
  onEdit,
  onToggle,
  onDelete,
  onTest,
}: AutomationCardProps) {
  return (
    <li
      data-testid={`automation-card-${automation.id}`}
      className={cn(
        'border rounded-xl p-3',
        automation.enabled
          ? 'border-line dark:border-line-dark'
          : 'border-dashed border-line dark:border-line-dark opacity-70'
      )}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <p className="font-medium text-sm text-ink-1 dark:text-ink-dark-1 truncate">
            {automation.nome}
          </p>
          <p className="text-xs text-ink-2 dark:text-ink-dark-2 mt-0.5">
            {automation.totalExecucoes === 1
              ? '1 disparo'
              : `${automation.totalExecucoes} disparos`}
            {automation.ultimaExecucaoIso
              ? ` · último ${formatRelativeDate(automation.ultimaExecucaoIso)}`
              : ''}
          </p>
        </div>
        <Badge variant={automation.enabled ? 'success' : 'neutral'} size="sm">
          {automation.enabled ? 'Ativa' : 'Pausada'}
        </Badge>
      </div>

      <p className="text-xs text-ink-2 dark:text-ink-dark-2 mb-1">
        {triggerSummary(automation.trigger, labelOf)}
      </p>
      <ul className="text-xs text-ink-2 dark:text-ink-dark-2 space-y-0.5">
        {automation.actions.slice(0, 2).map((a, idx) => (
          <li key={idx} className="flex flex-wrap items-center gap-1.5">
            <span>→ {actionSummary(a, labelOf)}</span>
            {acaoEmPreparacao(a) && (
              <Badge variant="warning" size="sm">
                Ainda não é enviada
              </Badge>
            )}
          </li>
        ))}
        {automation.actions.length > 2 && (
          <li>
            e mais {automation.actions.length - 2}{' '}
            {automation.actions.length - 2 === 1 ? 'ação' : 'ações'}
          </li>
        )}
      </ul>

      <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-line dark:border-line-dark">
        <Button size="sm" variant="ghost" onClick={onTest}>
          Testar
        </Button>
        <Button size="sm" variant="ghost" onClick={onEdit}>
          Editar
        </Button>
        <Button
          size="sm"
          variant="ghost"
          data-testid={`automation-toggle-${automation.id}`}
          onClick={onToggle}
        >
          {automation.enabled ? 'Pausar' : 'Ativar'}
        </Button>
        <Button
          size="sm"
          variant="destructive"
          data-testid={`automation-delete-${automation.id}`}
          onClick={onDelete}
          className="ml-auto"
        >
          Excluir
        </Button>
      </div>
    </li>
  );
}

// ─── History tab (50 últimas execuções por automation OU all) ─────────

interface HistoryTabProps {
  automations: Automation[];
  workflowSlug: string;
  filterAutomationId: string;
  onFilterChange: (id: string) => void;
}

function HistoryTab({
  automations,
  workflowSlug,
  filterAutomationId,
  onFilterChange,
}: HistoryTabProps) {
  return (
    <div className="space-y-3 mt-2">
      <div>
        <label className="text-xs text-ink-2 dark:text-ink-dark-2 block mb-1">
          Filtrar por automação
        </label>
        <Select
          value={filterAutomationId}
          onChange={(e) => onFilterChange(e.target.value)}
          data-testid="history-filter-automation"
        >
          <option value="all">Todas as automações</option>
          {automations.map((a) => (
            <option key={a.id} value={a.id}>
              {a.nome}
            </option>
          ))}
        </Select>
      </div>
      {filterAutomationId === 'all' ? (
        <HistoryAllAutomations automations={automations} workflowSlug={workflowSlug} />
      ) : (
        <HistorySingleAutomation automationId={filterAutomationId} />
      )}
    </div>
  );
}

function HistoryAllAutomations({
  automations,
  workflowSlug,
}: {
  automations: Automation[];
  workflowSlug: string; // mantém pra futuras queries consolidadas
}) {
  // Agrega via uma consulta por automação. Quando o volume crescer, vale um
  // endpoint consolidado (`/api/v1/automations/runs?workflow=`).
  void workflowSlug;
  if (automations.length === 0) {
    return (
      <p className="text-sm text-ink-2 dark:text-ink-dark-2 text-center py-4">
        Nenhuma automação configurada neste fluxo ainda.
      </p>
    );
  }
  return (
    <div className="space-y-3" data-testid="history-all">
      {automations.map((a) => (
        <HistoryGroupForAutomation key={a.id} automation={a} />
      ))}
    </div>
  );
}

function HistoryGroupForAutomation({ automation }: { automation: Automation }) {
  const runs = useAutomationRuns(automation.id, 10);
  if (runs.length === 0) return null;
  return (
    <div>
      <p className="text-xs font-semibold text-ink-2 dark:text-ink-dark-2 mb-1">
        {automation.nome}
      </p>
      <ul className="space-y-1">
        {runs.map((r) => (
          <RunRow key={r.id} run={r} />
        ))}
      </ul>
    </div>
  );
}

function HistorySingleAutomation({ automationId }: { automationId: string }) {
  const runs = useAutomationRuns(automationId, 50);
  if (runs.length === 0) {
    return (
      <p className="text-sm text-ink-2 dark:text-ink-dark-2 text-center py-4">
        Esta automação ainda não disparou nenhuma vez.
      </p>
    );
  }
  return (
    <ul className="space-y-1" data-testid="history-single">
      {runs.map((r) => (
        <RunRow key={r.id} run={r} />
      ))}
    </ul>
  );
}

/**
 * FASE-C: a linha do histórico mostrava `card 3f2a91c0-a1` (uuid cortado) e
 * um selo com o status cru do engine (`success` / `partial` / `error`); ao
 * abrir, cada linha era `send_email → ok`. Tudo em português agora, sem
 * identificador interno.
 */
const RUN_STATUS_LABEL: Record<AutomationRun['status'], string> = {
  success: 'Tudo certo',
  partial: 'Em parte',
  error: 'Falhou',
  'skipped-condition': 'Não se aplicava',
};

// `stub` é o status que o engine devolve pras ações que ele aceita mas ainda
// não envia (WhatsApp e e-mail) — na tela isso é "ainda não é enviada".
const ACTION_RESULT_LABEL: Record<AutomationRun['actionResults'][number]['status'], string> = {
  success: 'feito',
  stub: 'ainda não é enviada',
  skipped: 'não se aplicava',
  error: 'falhou',
};

function RunRow({ run }: { run: AutomationRun }) {
  const [expanded, setExpanded] = useState(false);
  // "Não se aplicava" não é erro — era pintado de vermelho junto com `error`.
  const variant =
    run.status === 'success'
      ? 'success'
      : run.status === 'partial'
        ? 'warning'
        : run.status === 'skipped-condition'
          ? 'neutral'
          : 'danger';
  return (
    <li
      data-testid={`run-row-${run.id}`}
      className="text-xs border border-line dark:border-line-dark rounded-xl px-3 py-2"
    >
      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        aria-expanded={expanded}
        className="w-full min-w-0 text-left flex items-center justify-between gap-2"
      >
        <span
          className="min-w-0 truncate text-ink-2 dark:text-ink-dark-2"
          title={formatDateTime(run.timestampIso)}
        >
          {formatRelativeDate(run.timestampIso)}
        </span>
        <Badge variant={variant} size="sm">
          {RUN_STATUS_LABEL[run.status]}
        </Badge>
      </button>
      {expanded && (
        <div className="mt-2 pt-2 border-t border-line dark:border-line-dark space-y-1">
          {run.actionResults.map((ar, i) => (
            <div key={i} className="text-xs text-ink-2 dark:text-ink-dark-2">
              <strong className="font-medium text-ink-1 dark:text-ink-dark-1">
                {ACTION_TYPE_LABELS[ar.action.type]}
              </strong>{' '}
              — {ACTION_RESULT_LABEL[ar.status]}
              {ar.detail && <span> · {ar.detail}</span>}
            </div>
          ))}
        </div>
      )}
    </li>
  );
}
